import { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import type { WorkoutPlanDto } from "../types";
import {
  type AthleteSummary,
  deleteAthletePlan,
  setCoachCode,
  watchAthletePlans,
  watchAthletes,
  watchCoachProfile,
} from "../db";
import { PlanEditor } from "./PlanEditor";

/**
 * The coach's side of the portal: the code athletes use to authorise them, and the athletes who
 * already have. Nobody appears here until they add the code from their own app.
 */
export function CoachView({ coach }: { coach: User }) {
  const [code, setCode] = useState("");
  const [codeDraft, setCodeDraft] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [athletes, setAthletes] = useState<AthleteSummary[]>([]);
  const [openAthlete, setOpenAthlete] = useState<AthleteSummary | null>(null);

  useEffect(
    () =>
      watchCoachProfile(coach.uid, (profile) => {
        setCode(profile.coachCode);
        setCodeDraft((current) => (current === "" ? profile.coachCode : current));
      }),
    [coach.uid],
  );

  useEffect(() => watchAthletes(coach.uid, setAthletes), [coach.uid]);

  async function saveCode() {
    setCodeError(null);
    try {
      await setCoachCode(coach.uid, codeDraft, code);
    } catch (cause) {
      setCodeError(cause instanceof Error ? cause.message : "Could not save the code.");
    }
  }

  if (openAthlete) {
    return (
      <AthletePlans
        coachUid={coach.uid}
        athlete={openAthlete}
        onBack={() => setOpenAthlete(null)}
      />
    );
  }

  return (
    <>
      <section className="card">
        <h2>Your coach code</h2>
        <p className="hint">
          Athletes type this into their app to let you write their plans. Change it whenever you
          like — the athletes you already have stay linked.
        </p>
        <div className="send">
          <label className="grow">
            Code
            <input
              value={codeDraft}
              placeholder="e.g. marco_rossi"
              onChange={(event) => setCodeDraft(event.target.value.trim())}
              spellCheck={false}
            />
          </label>
          <button onClick={saveCode} disabled={!codeDraft || codeDraft === code}>
            Save code
          </button>
        </div>
        {codeError && <p className="error">{codeError}</p>}
        {!code && (
          <p className="hint">
            You have no code yet, so no athlete can find you. Pick one to get started.
          </p>
        )}
      </section>

      <section className="card">
        <h2>Your athletes</h2>
        {athletes.length === 0 ? (
          <p className="hint">
            Nobody yet. An athlete appears here the moment they add your code in the app.
          </p>
        ) : (
          <ul className="athletes">
            {athletes.map((athlete) => (
              <li key={athlete.uid}>
                <button className="ghost row" onClick={() => setOpenAthlete(athlete)}>
                  <span>{athlete.username}</span>
                  <span className="chevron">→</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

/** An athlete's plans: what they already have, and the editor for adding or changing one. */
function AthletePlans({
  coachUid,
  athlete,
  onBack,
}: {
  coachUid: string;
  athlete: AthleteSummary;
  onBack: () => void;
}) {
  const [plans, setPlans] = useState<WorkoutPlanDto[]>([]);
  const [editing, setEditing] = useState<WorkoutPlanDto | "new" | null>(null);

  useEffect(() => watchAthletePlans(athlete.uid, setPlans), [athlete.uid]);

  if (editing) {
    return (
      <PlanEditor
        athleteUid={athlete.uid}
        athleteName={athlete.username}
        coachUid={coachUid}
        initialPlan={editing === "new" ? undefined : editing}
        onDone={() => setEditing(null)}
      />
    );
  }

  return (
    <section className="card">
      <div className="plan-head">
        <h2>{athlete.username}</h2>
        <button className="ghost" onClick={onBack}>
          Back to athletes
        </button>
      </div>

      {plans.length === 0 ? (
        <p className="hint">No plans yet.</p>
      ) : (
        <ul className="athletes">
          {plans.map((plan) => (
            <li key={plan.id}>
              <button className="ghost row" onClick={() => setEditing(plan)}>
                <span>
                  {plan.name || "Untitled"}{" "}
                  <span className="hint">— {plan.days?.length ?? 0} days</span>
                </span>
                <span className="chevron">edit</span>
              </button>
              <button
                className="ghost danger"
                onClick={() => deleteAthletePlan(athlete.uid, plan.id)}
                title="Delete plan"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <button onClick={() => setEditing("new")}>+ New plan</button>
    </section>
  );
}
